import React from 'react'
import ReactDOM from 'react-dom'
import styled from '@emotion/styled'
import { useSelector } from 'react-redux'
import { useSearchParams } from 'react-router'
import { useMutation } from '@tanstack/react-query'
import { Tray } from '@mobi/component-library/Overlays/Tray'
import { colors, font, spacing } from '@mobi/component-library/Theme/Common'
import { LoadingSpinner } from '@mobi/component-library/Feedback/LoadingSpinner'
import { addToast } from '@mobi/component-library/Feedback/Toast'
import { isRaceDetails } from '@mobi/betslip/helpers/typeGuards'
import { H3 } from '@core/Components/Text/Heading.styles'
import { getRaceLink } from '@core/Areas/Racing/helpers'
import { state$ as userAccountState$ } from '@core/State/UserAccount/userAccountDriver'
import {
  getFeaturesIsUserAnonymous,
  getFeatureStateInitializedFlag,
} from '@core/State/FeatureFlags/Store/selectors'
import { buildSharedBetSelections } from './helpers/buildSharedBetSelections'
import { getLoadBetErrorMessage } from '../LoadBet/helpers/loadBetErrors'
import { handleBetSelection } from '@core/Utils/betting/handleBetSelection'
import { appHistory } from '@core/Utils/routing/history'

const enum LocalConstants {
  ShareBetSearchKey = 'share-bet',
}

/** Contains listener and UI components to handle incoming share */
export const ShareBetIncoming: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const shareId = searchParams.get(LocalConstants.ShareBetSearchKey)

  const isLdInitialized = useSelector(getFeatureStateInitializedFlag)
  const isUserAnonymous = useSelector(getFeaturesIsUserAnonymous)

  const { isPending, mutate } = useMutation({
    mutationFn: buildSharedBetSelections,
    onSuccess(selections) {
      if (selections.length === 1 && isRaceDetails(selections[0].selectionDetails)) {
        const { meetingDate, meetingId, raceNumber } = selections[0].selectionDetails.races[0]
        setTimeout(() => appHistory.push(getRaceLink({ meetingDate, meetingId, raceNumber })), 500)
      }
      handleBetSelection({ betFlow: { location: 'Quickbet' }, selections })
    },
    onError(error) {
      const title = getLoadBetErrorMessage(error)
      const id = 'share-incoming-error'
      addToast({ id, timeoutMs: Infinity, isDismissable: true, title, icon: 'error' })
    },
  })

  // Capture and Process Share Bet Search Param
  React.useEffect(() => {
    if (!shareId || !isLdInitialized) return

    const capturedShareId = shareId
    setSearchParams({}, { replace: true })

    userAccountState$
      .skipWhile(x => !x.hasEvaluatedUserAuth || !!(x.isLoggedIn && isUserAnonymous))
      .take(1)
      .subscribe(() => mutate({ shareId: capturedShareId }))
  }, [shareId, setSearchParams, isUserAnonymous, isLdInitialized, mutate])

  return ReactDOM.createPortal(
    <Tray isOpen={isPending} headerTitle='' dismissStatus='disallowed'>
      <WrapperStyled>
        <div>
          <LoadingSpinner size='5rem' color='purple' />
          <div>
            <H3>Opening a Shared Bet</H3>
            <p>This may take a couple of seconds</p>
          </div>
        </div>
      </WrapperStyled>
    </Tray>,
    document.body
  )
}

// ======
// Styles
// ======

const WrapperStyled = styled.div({
  fontFamily: font.family.primary,

  '> div ': {
    display: 'flex',
    gap: spacing.bigx2,
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',

    '> div': {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      flexDirection: 'column',

      '> h3': {
        fontSize: font.size.xl2.fontSize,
        fontWeight: font.weight.semibold,
        lineHeight: font.size.xl2.lineHeight,
        letterSpacing: font.size.xl2.letterSpacing,
        margin: 0,
      },

      '> p': {
        color: colors.neutral[700],
        fontFamily: font.family.primary,
        fontSize: font.size.md.fontSize,
        fontWeight: font.weight.regular,
        lineHeight: font.size.md.lineHeight,
        margin: 0,
      },
    },
  },
})
